import { FC, Fragment, JSX, useContext, useEffect, useRef, useState } from "react";
import { AccessReportStatusEnum } from "@app/access-reports/dashboard";
import { EmployeeGroupExample, EmployeeGroupExampleFileNameEnum } from "@app/access-reports/wizard";
import { Message, MessageType } from "@app/notifications";
import { AccessReportsEmployeeGroupUploadStatusChangePayload } from "@app/notifications/message";
import { DragAndDropFileUploaderStateEnum } from "@app/shared/interfaces/dragAndDropUploader";
import { NotificationContext } from "@app/notifications/NotificationContext";
import DragAndDropFileUploader from "@app/shared/DragAndDropFileUploader";
import LoadingBox from "@app/shared/LoadingBox";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import AccessReportsService from "@app/access-reports/accessReportsService";
import { EmployeeGroup } from "@app/access-reports/shared/employeeGroup";

type AccessReportsWizardStepPopulationProps = {
  employeeGroup: EmployeeGroup;
  employeeGroupExamples: EmployeeGroupExample[];
  employeeGroupUploadStatus: AccessReportStatusEnum;
  employeeGroupUploadMessage: string;
  setEmployeeGroupUploadStatus: (employeeGroupUploadStatus: AccessReportStatusEnum, employeeGroupUploadMessage: string) => void;
  setEmployeeGroupUploadValues: (employeeGroup: EmployeeGroup) => void;
}

export const AccessReportsWizardStepPopulation: FC<AccessReportsWizardStepPopulationProps> = (props: AccessReportsWizardStepPopulationProps) => {
  const accessReportsService = useRef(new AccessReportsService()).current;
  const notificationContext = useContext(NotificationContext);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    notificationContext.subscribe(handleNotifications);

    return () => {
      notificationContext.unsubscribe(handleNotifications);
    }
  }, []);

  const getEmployeeGroupExamples = (): React.ReactNode => {
    if (!!props.employeeGroupExamples && props.employeeGroupExamples.length) {
      const employeeGroupExamplePrefix = " Preformatted template downloads: ";
      const employeeGroupExampleLink: React.ReactNode[] = [employeeGroupExamplePrefix];

      for (let i = 0; i < props.employeeGroupExamples.length; i++) {
        let title = "";

        switch (props.employeeGroupExamples[i].fileName) {
          case EmployeeGroupExampleFileNameEnum.CsvUploadSamples:
            title = "CSV";
            break;
          case EmployeeGroupExampleFileNameEnum.ExcelUploadSamples:
            title = "Excel";
            break;
          default:
            title = "Invalid File Name";
            break;
        }

        if (i !== 0 && props.employeeGroupExamples.length > 1) {
          employeeGroupExampleLink.push(
            <span key={i}> and <a className="demibold" href={props.employeeGroupExamples[i].link} id={`access-reports-wizard__population--example-${i}`}>{title}</a></span>
          );
        } else {
          employeeGroupExampleLink.push(
            <a key={i} className="demibold" href={props.employeeGroupExamples[i].link} id={`access-reports-wizard__population--example-${i}`}>{title}</a>
          );
        }
      }

      return employeeGroupExampleLink;
    }
  }

  const getEmployeeGroupStatus = (): JSX.Element => {
    if (props.employeeGroupUploadStatus === AccessReportStatusEnum.Running) {
      return (
        <div className="d-flex justify-content-center">
          <LoadingSpinner />
          <h4 className="align-self-center header-s ms-2 mb0">
            Your file is being processed
          </h4>
        </div>
      );
    } else {
      let alertText = "";
      let alertType: "warning" | "info" | "danger" | "success";

      switch (props.employeeGroupUploadStatus) {
        case AccessReportStatusEnum.Canceled:
          alertText = "The processing of your file has been canceled."
          alertType = "warning";
          break;
        case AccessReportStatusEnum.Created:
          alertText = "The processing of your file has been created."
          alertType = "info";
          break;
        case AccessReportStatusEnum.Failed:
          alertText = "The processing of your file has failed.";
          if (props.employeeGroupUploadMessage !== null) {
            alertText += " " + props.employeeGroupUploadMessage;
          }
          alertType = "danger";
          break;
        case AccessReportStatusEnum.Succeeded:
          alertText = "Your file has been successfully processed.";
          if (props.employeeGroupUploadMessage !== null) {
            alertText += " " + props.employeeGroupUploadMessage;
          }
          alertType = "success";
          break;
        default:
          break;
      }

      return (
        <QESAlert classNames="mb-3" type={alertType} persist={true}>
          {alertText}
        </QESAlert>
      );
    }
  }

  const handleNotifications = async (message: Message): Promise<void> => {
    if (message?.notificationType === MessageType.AccessReportsEmployeeGroupUploadStatusChange) {
      const payload: AccessReportsEmployeeGroupUploadStatusChangePayload = message.payload as AccessReportsEmployeeGroupUploadStatusChangePayload;

      if (!!payload) {
        props.setEmployeeGroupUploadStatus(payload.status, payload.uploadMessage);

        if (payload.status === AccessReportStatusEnum.Succeeded) {
          const employeeGroup: EmployeeGroup = {
            clientId: payload.clientId,
            dateCreated: payload.dateCreated,
            employeeGroupId: payload.employeeGroupId,
            employeeGroupStatus: payload.employeeGroupStatus,
            employeeGroupType: payload.employeeGroupType,
            fileName: payload.fileName,
            userId: payload.userId,
          }

          props.setEmployeeGroupUploadValues(employeeGroup);
        }
      }
    }
  }

  const onUpload = async (data: FormData): Promise<void> => {
    return new Promise<void>(async (resolve, reject) => {
      setLoading(true);


      try {
        const employeeGroupId: number = await accessReportsService.createEmployeeGroupFromFile(data);

        if (!!employeeGroupId.toString()) {
          props.setEmployeeGroupUploadStatus(AccessReportStatusEnum.Running, null);
        }

        resolve();
      } catch {
        reject();
      } finally {
        setLoading(false);
      }
    });
  }

  const onUploadStateChange = (uploadState: DragAndDropFileUploaderStateEnum) => {
    if (uploadState === DragAndDropFileUploaderStateEnum.Pending) {
      props.setEmployeeGroupUploadValues(null);
      props.setEmployeeGroupUploadStatus(null, null);
    }
  }

  return (
    <Fragment>
      <div className="row">
        <div className="col-12">
          <p className="copy-text-default mb0">
            Upload a Census File: Please Upload a .CSV or .XLSX file that includes the information needed. <span className="demibold text-danger">Do not upload a PII or PHI file.</span>
            {getEmployeeGroupExamples()}
          </p>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <LoadingBox
            show={loading}
          />

          <DragAndDropFileUploader
            apiKeyName="employeeGroupFile"
            title=""
            filetype=".csv,.xlsx"
            hasBeenSuccessful={!!props.employeeGroup}
            onUpload={onUpload}
            onUploadStateChange={onUploadStateChange}
            testId="access-reports-wizard__population--file-upload"
          />

          {!!props.employeeGroupUploadStatus && getEmployeeGroupStatus()}
        </div>
      </div>
    </Fragment>
  );
}
